import React, { useState } from 'react';
import {
    Card,
    Button,
    Typography,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    message,
    Spin,
    Statistic,
    Row,
    Col,
    Alert,
} from 'antd';
import {
    TeamOutlined,
    PlusOutlined,
    ShopOutlined,
    UserOutlined,
    MailOutlined,
    UserSwitchOutlined,
} from '@ant-design/icons';
import {
    useGetAdminAgenciesQuery,
    useCreateAdminAgencyMutation,
} from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { Utils } from '../../utils/utils';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { ImpersonationModal } from '../components/ImpersonationModal';

const { Title, Text } = Typography;

export const AdminAgenciesPage: React.FC = () => {
    const { user } = useAuth();
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [impersonationModalVisible, setImpersonationModalVisible] = useState(false);
    const [selectedAgency, setSelectedAgency] = useState<{ id: number; name: string; userCount: number } | null>(null);
    const [form] = Form.useForm();

    const canImpersonate = Utils.userIsPlatformAdmin(user);

    const { data: agencies, isLoading, refetch } = useGetAdminAgenciesQuery({
        jwtToken: user?.jwtToken || '',
    });

    const [createAgency, { isLoading: isCreating }] = useCreateAdminAgencyMutation();

    const handleCreate = async () => {
        try {
            const values = await form.validateFields();
            const result = await createAgency({
                jwtToken: user?.jwtToken || '',
                payload: values,
            }).unwrap();

            message.success(`Agency "${result.name}" created successfully`);

            if (result.adminUser?.resetToken) {
                const resetLink = `${window.location.origin}/password-set/${result.adminUser.resetToken}`;
                Modal.info({
                    title: 'Agency Admin User Created',
                    content: (
                        <div>
                            <p>Admin user <strong>{result.adminUser.email}</strong> has been created.</p>
                            <p>They can set their password at:</p>
                            <Input.TextArea
                                value={resetLink}
                                rows={2}
                                readOnly
                                onClick={(e) => {
                                    (e.target as HTMLTextAreaElement).select();
                                    navigator.clipboard.writeText(resetLink);
                                    message.success('Link copied to clipboard');
                                }}
                            />
                        </div>
                    ),
                    width: 500,
                });
            }

            form.resetFields();
            setCreateModalVisible(false);
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to create agency');
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => (
                <Space>
                    <TeamOutlined style={{ color: '#0369a1' }} />
                    <Text strong>{name}</Text>
                </Space>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>{status}</Tag>
            ),
        },
        {
            title: 'Clients',
            dataIndex: 'clientCount',
            key: 'clientCount',
            render: (count: number) => (
                <Space>
                    <ShopOutlined />
                    {count}
                </Space>
            ),
        },
        {
            title: 'Users',
            dataIndex: 'userCount',
            key: 'userCount',
            render: (count: number) => (
                <Space>
                    <UserOutlined />
                    {count}
                </Space>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => new Date(date).toLocaleDateString(),
        },
        ...(canImpersonate
            ? [
                  {
                      title: 'Actions',
                      key: 'actions',
                      width: 160,
                      render: (_: unknown, record: any) => {
                          const disabled = record.status !== 'ACTIVE' || !record.userCount;
                          return (
                              <Button
                                  size="small"
                                  icon={<UserSwitchOutlined />}
                                  disabled={disabled}
                                  onClick={() => {
                                      setSelectedAgency({
                                          id: record.id,
                                          name: record.name,
                                          userCount: record.userCount,
                                      });
                                      setImpersonationModalVisible(true);
                                  }}
                              >
                                  Impersonate
                              </Button>
                          );
                      },
                  },
              ]
            : []),
    ];

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>
                    <TeamOutlined style={{ marginRight: 8 }} />
                    Agencies
                </Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                >
                    Add Agency
                </Button>
            </div>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={8}>
                    <Card>
                        <Statistic
                            title="Total Agencies"
                            value={agencies?.length || 0}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8}>
                    <Card>
                        <Statistic
                            title="Total Agency Clients"
                            value={agencies?.reduce((sum: number, a: any) => sum + a.clientCount, 0) || 0}
                            prefix={<ShopOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Card>
                <ResponsiveTable
                    columns={columns}
                    dataSource={agencies || []}
                    rowKey="id"
                    pagination={{ pageSize: 20 }}
                />
            </Card>

            <Modal
                title={
                    <>
                        <TeamOutlined style={{ marginRight: 8 }} />
                        Add Agency
                    </>
                }
                open={createModalVisible}
                onOk={handleCreate}
                onCancel={() => { form.resetFields(); setCreateModalVisible(false); }}
                confirmLoading={isCreating}
                okText="Create Agency"
                width={500}
            >
                <Alert
                    type="info"
                    message="This will create a new agency organisation with an admin user who can manage client relationships."
                    style={{ marginBottom: 16 }}
                    showIcon
                />
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Agency Name"
                        rules={[{ required: true, message: 'Agency name is required' }]}
                    >
                        <Input prefix={<TeamOutlined />} placeholder="e.g. Bright Marketing Agency" />
                    </Form.Item>

                    <Form.Item
                        name="billingEmail"
                        label="Billing Email"
                    >
                        <Input prefix={<MailOutlined />} placeholder="billing@agency.com" type="email" />
                    </Form.Item>

                    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16, marginTop: 8 }}>
                        <Text strong>Agency Admin User</Text>
                    </div>

                    <Form.Item
                        name="adminEmail"
                        label="Admin Email"
                        rules={[{ required: true, message: 'Admin email is required' }]}
                        style={{ marginTop: 12 }}
                    >
                        <Input prefix={<MailOutlined />} placeholder="admin@agency.com" type="email" />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: 16 }}>
                        <Form.Item
                            name="adminFirstName"
                            label="First Name"
                            rules={[{ required: true, message: 'Required' }]}
                            style={{ flex: 1 }}
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name="adminLastName"
                            label="Last Name"
                            rules={[{ required: true, message: 'Required' }]}
                            style={{ flex: 1 }}
                        >
                            <Input />
                        </Form.Item>
                    </div>
                </Form>
            </Modal>

            <ImpersonationModal
                visible={impersonationModalVisible}
                onCancel={() => {
                    setImpersonationModalVisible(false);
                    setSelectedAgency(null);
                }}
                organization={selectedAgency}
            />
        </div>
    );
};
